import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, RotateCcw, Volume2 } from 'lucide-react';

interface AudioPlayerProps {
  files: string[];
  onComplete?: () => void;
}

export function AudioPlayer({ files, onComplete }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const totalTracks = files.length;
  const currentFile = files[currentTrack];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (currentTrack < totalTracks - 1) {
        // Go to next track
        setCurrentTrack((t) => t + 1);
        setCurrentTime(0);
      } else {
        // Playlist finished
        setIsPlaying(false);
        onComplete?.();
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack, totalTracks, onComplete]);

  // Auto-play when track changes (if was playing)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.load();
    if (isPlaying) {
      audio.play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const restart = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = 0;
    audio.play();
    setIsPlaying(true);
  };

  const prevTrack = () => {
    if (currentTrack > 0) {
      setCurrentTrack((t) => t - 1);
      setCurrentTime(0);
    }
  };

  const nextTrack = () => {
    if (currentTrack < totalTracks - 1) {
      setCurrentTrack((t) => t + 1);
      setCurrentTime(0);
    }
  };

  const changeSpeed = (rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.playbackRate = rate;
    setPlaybackRate(rate);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Get track title from filename
  const getTrackTitle = (filename: string) => {
    const name = filename.replace('.mp3', '');
    if (name.startsWith('grundsatz')) {
      return `Grundsatz ${name.replace('grundsatz', '')}`;
    }
    if (name.startsWith('vorteil')) {
      return `Vorteil ${name.replace('vorteil', '')}`;
    }
    if (name.startsWith('nachteil')) {
      return `Nachteil ${name.replace('nachteil', '')}`;
    }
    if (name.startsWith('lektion')) {
      return `Lektion ${name.replace('lektion', '')}`;
    }
    return name;
  };

  return (
    <div className="audio-player">
      <audio ref={audioRef} src={`/audio/${currentFile}`} preload="metadata" />

      {totalTracks > 1 && (
        <div className="track-info">
          <span className="track-title">{getTrackTitle(currentFile)}</span>
          <span className="track-counter">Track {currentTrack + 1} von {totalTracks}</span>
        </div>
      )}

      <div className="audio-controls">
        {totalTracks > 1 && (
          <button
            className="audio-btn"
            onClick={prevTrack}
            disabled={currentTrack === 0}
          >
            <SkipBack size={20} />
          </button>
        )}

        <button className="audio-btn play-btn" onClick={togglePlay}>
          {isPlaying ? <Pause size={24} /> : <Play size={24} />}
        </button>

        {totalTracks > 1 && (
          <button
            className="audio-btn"
            onClick={nextTrack}
            disabled={currentTrack === totalTracks - 1}
          >
            <SkipForward size={20} />
          </button>
        )}

        <button className="audio-btn" onClick={restart}>
          <RotateCcw size={20} />
        </button>

        <div className="audio-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        <div className="speed-controls">
          {[0.75, 1, 1.25].map((rate) => (
            <button
              key={rate}
              className={`speed-btn ${playbackRate === rate ? 'active' : ''}`}
              onClick={() => changeSpeed(rate)}
            >
              {rate}x
            </button>
          ))}
        </div>

        <Volume2 size={18} className="volume-icon" />
      </div>
    </div>
  );
}
